import ReactTooltip from 'react-tooltip';
import React, { useMemo, useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';

import { getAllocationAppendLabel } from 'utils/user';

import { AllocationItem } from 'interface/allocation';
import CurrentAllocation from 'components/common/currentAllocation';

type AppendLabel = {
  singular?: string;
  plural?: string;
};

type ChartDataItem = {
  key: number;
  label: string;
  value: number;
  color: string;
};

interface Props {
  chartData?: ChartDataItem[];
  className?: string;
  appendLabel?: AppendLabel | string;
  colors?: string[];
  projectAllocations?: AllocationItem[];
}

export default function CommonChart({
  chartData = [],
  className = '',
  appendLabel = '',
  colors = [],
  projectAllocations = [],
}: Props) {
  const [hovered, setHovered] = useState<number | null>(null);
  const emptyData: ChartDataItem[] = [
    {
      key: 0,
      label: '',
      value: 100,
      color: '#F6F6F6',
    },
  ];

  const totalValue = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  function makeTooltipContent(entry: ChartDataItem) {
    const resolvedAppend: AppendLabel | undefined =
      typeof appendLabel === 'string' ? { singular: appendLabel, plural: appendLabel } : appendLabel;
    const suffix = entry.value === 1 ? resolvedAppend?.singular : resolvedAppend?.plural;
    return <div className="piechart__tooltip">{`${entry.label} - ${entry.value} ${suffix || ''}`}</div>;
  }

  return (
    <div className={`piechart__content align-items-center ${className}`}>
      <div className="piechart__main" data-tip="" data-for="chart">
        <PieChart
          animate={true}
          startAngle={270}
          data={chartData.length === 0 ? emptyData : chartData}
          totalValue={totalValue}
          lineWidth={30}
          onMouseOver={(_, index) => {
            setHovered(index);
          }}
          onMouseOut={() => {
            setHovered(null);
          }}
          labelPosition={0}
          label={({ x, y, dx, dy, dataEntry }) => {
            if (dataEntry.key === 0) {
              return (
                <text x={x} y={y} dx={dx} dy={dy} dominantBaseline="middle" textAnchor="middle">
                  <tspan x="50%" dy="-5" className="piechart__label">
                    {totalValue}
                  </tspan>
                  {appendLabel && (
                    <tspan x="50%" dy="17" className="piechart__hrsLabel">
                      {getAllocationAppendLabel(totalValue)}
                    </tspan>
                  )}
                </text>
              );
            }
          }}
        />
        {chartData.length !== 0 && (
          <ReactTooltip
            id="chart"
            multiline={true}
            getContent={() => (typeof hovered === 'number' ? makeTooltipContent(chartData[hovered]) : null)}
          />
        )}
      </div>
      <div className="piechart__wrapper custom-scroll-bar">
        <div className="piechart__legend">
          <CurrentAllocation allocations={projectAllocations} colorData={colors} />
        </div>
      </div>
    </div>
  );
}
